import { useState } from 'react';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import type { CreateUserBody, TfUserRow } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { hub } from '@/lib/hub-client';

export interface CreateUserFormValues {
  email: string;
  name: string;
  password: string;
  admin: boolean;
}

/** The shortest password `CreateUserBodySchema` accepts. */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * The `POST /users` body. The password is always sent: this dialog exists
 * for deployments without a mail provider, where a password-less user could
 * never be invited and would be locked out. An empty name is omitted, not
 * sent as `""`. Roles are only sent for an admin — the hub defaults everyone
 * else to `member`.
 */
export function createUserBody(v: CreateUserFormValues): CreateUserBody {
  const name = v.name.trim();
  return {
    email: v.email.trim(),
    ...(name ? { name } : {}),
    password: v.password,
    ...(v.admin ? { roles: ['admin'] } : {}),
  };
}

export function canSubmitCreateUser(v: Pick<CreateUserFormValues, 'email' | 'password'>): boolean {
  return v.email.trim() !== '' && v.password.length >= MIN_PASSWORD_LENGTH;
}

function createErrorMessage(err: unknown): string {
  if (err instanceof HubClientError && err.code === 'email_taken') {
    return 'A user with this email already exists.';
  }
  return (err as Error).message ?? 'Create failed';
}

const EMPTY: CreateUserFormValues = { email: '', name: '', password: '', admin: false };

export interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (user: TfUserRow) => void;
}

export function CreateUserDialog({ open, onClose, onCreated }: CreateUserDialogProps) {
  const [values, setValues] = useState<CreateUserFormValues>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof CreateUserFormValues>(key: K, value: CreateUserFormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const close = () => {
    setValues(EMPTY);
    setError(null);
    onClose();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { user } = await hub.usersCreate({ body: createUserBody(values) });
      onCreated(user);
      close();
    } catch (err) {
      setError(createErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Create user"
      description="The account is active at once and can sign in with this password. Use this instead of an invite when the hub cannot send email."
      footer={
        <>
          <Button variant="outline" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="create-user-form" disabled={busy || !canSubmitCreateUser(values)}>
            {busy ? 'Creating…' : 'Create user'}
          </Button>
        </>
      }
    >
      <form id="create-user-form" className="space-y-4" onSubmit={submit}>
        <div className="space-y-2">
          <Label htmlFor="create-email">Email</Label>
          <Input
            id="create-email"
            type="email"
            autoComplete="off"
            required
            autoFocus
            value={values.email}
            onChange={(e) => set('email', e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="create-name">Name</Label>
          <Input id="create-name" autoComplete="off" value={values.name} onChange={(e) => set('name', e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="create-password">Password</Label>
          <Input
            id="create-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={values.password}
            onChange={(e) => set('password', e.target.value)}
          />
          <p className="text-xs text-muted-foreground">At least {MIN_PASSWORD_LENGTH} characters.</p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={values.admin} onCheckedChange={(checked) => set('admin', checked)} />
          System admin
        </label>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </form>
    </Dialog>
  );
}
